import { Link } from "react-router-dom";
import { Instagram, Linkedin, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { FIRM, TEL_LINK, waLink, WA_MESSAGES } from "../data/firm";
import { CATEGORIES } from "../data/services";

export const Footer = () => (
  <footer className="bg-navy text-slate-300" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="bg-white p-1.5">
              <img src="/assets/ca-india-logo.webp" alt="CA India" className="h-8 w-auto" loading="lazy" />
            </span>
            <span className="leading-tight">
              <span className="block font-display text-lg font-bold text-white">Aniruddha Sonar &amp; Associates</span>
              <span className="block text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                Chartered Accountants
              </span>
            </span>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-slate-400">
            A Chartered Accountancy practice established in 2024, providing taxation, audit, compliance and business
            advisory services from Vashi, Navi Mumbai.
          </p>
          <div className="mt-5 flex gap-3">
            <a
              href={FIRM.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="footer-instagram-link"
              className="flex h-9 w-9 items-center justify-center border border-slate-600 text-slate-300 transition-colors hover:border-corpblue-500 hover:text-white"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href={FIRM.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              data-testid="footer-linkedin-link"
              className="flex h-9 w-9 items-center justify-center border border-slate-600 text-slate-300 transition-colors hover:border-corpblue-500 hover:text-white"
            >
              <Linkedin className="h-4 w-4" />
            </a>
            <a
              href={waLink(WA_MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              data-testid="footer-whatsapp-link"
              className="flex h-9 w-9 items-center justify-center border border-slate-600 text-slate-300 transition-colors hover:border-corpblue-500 hover:text-white"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.24em] text-white">Firm</h3>
          <ul className="mt-5 space-y-2.5 text-sm">
            {[
              ["About the Firm", "/about"],
              ["Services", "/services"],
              ["Industries We Serve", "/industries"],
              ["Insights", "/insights"],
              ["Careers", "/careers"],
              ["Contact", "/contact"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link to={to} className="link-underline transition-colors hover:text-white" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.24em] text-white">Services</h3>
          <ul className="mt-5 space-y-2.5 text-sm">
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <Link to={`/services#${c.slug}`} className="link-underline transition-colors hover:text-white" data-testid={`footer-service-${c.slug}`}>
                  {c.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.24em] text-white">Head Office</h3>
          <ul className="mt-5 space-y-4 text-sm">
            <li className="flex gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-corpblue-500" />
              <span>{FIRM.addressShort}</span>
            </li>
            <li>
              <a href={TEL_LINK} className="flex gap-3 transition-colors hover:text-white" data-testid="footer-phone-link">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-corpblue-500" />
                {FIRM.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={`mailto:${FIRM.email}`} className="flex gap-3 break-all transition-colors hover:text-white" data-testid="footer-email-link">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-corpblue-500" />
                {FIRM.email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-slate-700/60 pt-7 text-xs text-slate-500 md:flex-row md:items-center">
        <p data-testid="footer-copyright">
          © {new Date().getFullYear()} {FIRM.name}, {FIRM.designation}. All rights reserved.
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Link to="/privacy-policy" className="transition-colors hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/disclaimer" className="transition-colors hover:text-white" data-testid="footer-disclaimer-link">Disclaimer</Link>
          <Link to="/terms-of-use" className="transition-colors hover:text-white" data-testid="footer-terms-link">Terms of Use</Link>
        </div>
      </div>
      <p className="mt-5 text-[11px] leading-relaxed text-slate-600">
        The contents of this website are for information purposes only and do not constitute professional advice.
        External government portals linked from this website are operated by their respective authorities.
      </p>
    </div>
  </footer>
);
